import { useState, useMemo } from 'react'
import { useSelector } from 'react-redux'
import {
  translatedStatus,
  translatedSeason,
  daysTranslation,
} from '../../data/translationMap'
import { openNotRegisteredPopup } from '../../store/slices/ui.slice'
import { WatchingOnline } from '../../assets/imgs/svg/AnimeDetails/desktop/WatchingOnline'
import { AddToWatch } from '../../assets/imgs/svg/AnimeDetails/desktop/AddToWatch'
import { PlanToWatch } from '../../assets/imgs/svg/AnimeDetails/desktop/PlanToWatch'
import { Year } from '../../assets/imgs/svg/AnimeDetails/desktop/Year'
import { ReviewPopup } from '../ReviewSystem/ReviewPopup'
import { AddReview } from '../../assets/imgs/svg/AnimeDetails/desktop/AddReview'
import { EditReview } from '../../assets/imgs/svg/AnimeDetails/desktop/EditReview'
import { EditWatchlist } from '../../assets/imgs/svg/AnimeDetails/desktop/EditWatchlist'
import toast from 'react-hot-toast'
import { AnimeWatchlistEdit } from '../UserProfile/UserWatchlist/AnimeWatchlistEdit'
import { useWatchlistActions } from '../../hooks/useWatchlistActions'
import { getAnimeImage } from '../../services/util.service'
import { MinusIcon, PlusIcon } from '../ui/icons/shared'
import { Button } from '../ui/elements'

export function AnimeHeader({
  anime,
  handleAddItem,
  loggedInUser,
  dispatch,
  isInWatchlist,
  currentWatchItem,
  onIncrement,
  onDecrement,
  onRemove,
  onSaveChanges,
  canEdit,
}) {
  const [showReviewPopup, setShowReviewPopup] = useState(false)
  const [initialReviewData, setInitialReviewData] = useState(null)
  const [showEditWatchlist, setShowEditWatchlist] = useState(false)
  const [editData, setEditData] = useState(null)

  const reviews = useSelector((state) => state.reviewModule.reviews) || []

  const { getUpdatedWatchItem } = useWatchlistActions(loggedInUser?._id)

  const filteredReviews = reviews.filter(
    (review) => review.animeId === anime.anizone_id
  )

  const userAlreadyReviewed = useMemo(() => {
    if (!loggedInUser) return false
    return filteredReviews.some((review) => review.userId === loggedInUser._id)
  }, [filteredReviews, loggedInUser])

  const statuses = [
    { value: 'Watching', label: 'כרגע צופה' },
    { value: 'Plan to Watch', label: 'מתכנן לצפות' },
    { value: 'Completed', label: 'סיימתי לצפות' },
    { value: 'On-Hold', label: 'בהמתנה' },
    { value: 'Dropped', label: 'ננטש' },
  ]

  const currentStatusLabel = currentWatchItem
    ? statuses.find((status) => status.value === currentWatchItem.status)?.label
    : null

  const handleWriteReview = () => {
    if (!loggedInUser) {
      toast.error('עליך להיות מחובר כדי לכתוב ביקורת.')
      dispatch(openNotRegisteredPopup())
      return
    }

    const existingReview = filteredReviews.find(
      (review) =>
        review.animeId === anime.anizone_id &&
        review.userId === loggedInUser._id
    )
    setInitialReviewData(existingReview || null)
    setShowReviewPopup(true)
  }

  const handleEditWatchlist = () => {
    if (!currentWatchItem) return
    setEditData({
      progress: currentWatchItem.progress,
      status: currentWatchItem.status,
    })
    setShowEditWatchlist(true)
  }

  return (
    <div className="w-[226px]">
      <div className="max-h-full sticky top-[100px]">
        <div className="w-full h-[350px] relative">
          <div className="absolute -translate-y-[40%] right-[-10%]">
            <Year anime={anime} />
          </div>
          <img
            src={getAnimeImage(anime)}
            className="w-full h-full object-cover"
            alt={`${anime.title_english || anime.title} Cover Image`}
          />
        </div>

        <div className="flex text-center justify-between my-6">
          <div>
            <p className="text-muted text-sm">עונה</p>
            <span className="text-softText text-lg">
              {translatedSeason[anime.season] || anime.season || 'לא צויין'}
            </span>
          </div>
          <div>
            <p className="text-muted text-sm">זמני שידור</p>
            <span className="text-softText text-lg">
              {daysTranslation[anime.broadcast?.day] ||
                anime.broadcast?.day ||
                'לא צויין'}
              {anime.broadcast?.time ? `, ${anime.broadcast.time}` : ''}
            </span>
          </div>
          <div>
            <p className="text-muted text-sm">סטטוס</p>
            <span className="text-softText text-lg">
              {translatedStatus[anime.status] || anime.status || 'לא צויין'}
            </span>
          </div>
        </div>

        <div className="flex flex-col justify-center items-center gap-4">
          {!isInWatchlist && (
            <>
  

              <Button
                text="הוספה לרשימה"
                width={227}
                height={60}
                fontSize="text-lg"
                textColor="text-buttonSecondary1"
                bgColor="fill-accentLight"
                onClick={() => handleAddItem('Watching', anime)}
                icon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="22"
                    viewBox="0 0 17 22"
                    fill="none"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M3.26367 0.00390745C1.51293 0.0039076 0.0937539 1.42313 0.0937541 3.17383L0.0937556 20.0801C0.0937556 20.4568 0.294306 20.8049 0.620069 20.994C0.945831 21.183 1.34756 21.1844 1.67459 20.9975L8.54688 17.0706L15.4191 20.9975C15.7462 21.1844 16.1479 21.183 16.4737 20.994C16.7995 20.8049 17 20.4568 17 20.0801L17 3.17383C17 1.42313 15.5808 0.00390637 13.8301 0.00390653L3.26367 0.00390745ZM12.7734 8.45703C12.7734 7.87347 12.3004 7.40039 11.7168 7.40039L9.60352 7.40039L9.60352 5.28711C9.60352 4.70355 9.13046 4.23047 8.54688 4.23047C7.96329 4.23047 7.49024 4.70355 7.49024 5.28711L7.49024 7.40039L5.37696 7.40039C4.79337 7.40039 4.32032 7.87347 4.32032 8.45703C4.32032 9.04061 4.79337 9.51367 5.37696 9.51367L7.49024 9.51367L7.49024 11.627C7.49024 12.2105 7.9633 12.6836 8.54688 12.6836C9.13046 12.6836 9.60352 12.2105 9.60352 11.627L9.60352 9.51367L11.7168 9.51367C12.3004 9.51367 12.7734 9.04061 12.7734 8.45703Z"
                      fill="#F0ED9C"
                    />
                  </svg>
                }
                aria-label="הוסף לרשימת צפייה"
                title="הוספה לרשימת צפייה"
              />
                     <Button
                text="לצפייה בעתיד"
                width={227}
                height={60}
                fontSize="text-lg"
                textColor="text-buttonSecondary1"
                bgColor="fill-accentLight"
                onClick={() => handleAddItem('Plan to Watch', anime)}
                icon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="15"
                    height="13"
                    viewBox="0 0 15 13"
                    fill="none"
                  >
                    <path
                      d="M0.913332 10.3439H11.7572C11.8544 10.3439 11.9484 10.3089 12.023 10.245C12.1271 10.1553 14.503 8.05133 14.6479 3.50977H3.00182C2.85751 7.63229 0.669428 9.56999 0.646689 9.58933C0.513382 9.70528 0.464335 9.89467 0.524308 10.0632C0.583867 10.2313 0.739083 10.3439 0.913332 10.3439Z"
                      fill="#F0ED9C"
                    />
                    <path
                      d="M14.2468 0.94704H12.172V0.519907C12.172 0.280712 11.9895 0.0927734 11.7571 0.0927734C11.5247 0.0927734 11.3422 0.280712 11.3422 0.519907V0.94704H9.23977V0.519907C9.23977 0.280712 9.0572 0.0927734 8.82483 0.0927734C8.59246 0.0927734 8.40989 0.280712 8.40989 0.519907V0.94704H6.33517V0.519907C6.33517 0.280712 6.1526 0.0927734 5.92023 0.0927734C5.68786 0.0927734 5.50528 0.280712 5.50528 0.519907V0.94704H3.43057C3.1982 0.94704 3.01562 1.13498 3.01562 1.37417V2.65557H14.6617V1.37417C14.6617 1.13498 14.4791 0.94704 14.2468 0.94704Z"
                      fill="#F0ED9C"
                    />
                    <path
                      d="M12.5558 10.8998C12.3305 11.0929 12.0476 11.1981 11.7571 11.1981H3.01562V12.4795C3.01562 12.7156 3.20122 12.9066 3.43057 12.9066H14.2468C14.4761 12.9066 14.6617 12.7156 14.6617 12.4795V7.69141C13.8617 9.71178 12.7453 10.7365 12.5558 10.8998Z"
                      fill="#F0ED9C"
                    />
                  </svg>
                }
                aria-label="הוסף לצפייה בעתיד"
                title="לצפייה בעתיד"
              />
            </>
          )}

          {isInWatchlist && (
            <>
              <span className="w-full h-[2px] bg-[rgba(255,255,255,0.15)]"></span>
              {currentStatusLabel && (
                <div className="flex flex-col items-center gap-2 justify-center">
                  <span className="text-softText text-lg">
                    {currentStatusLabel}
                  </span>
                </div>
              )}

              {currentWatchItem && (
                <div className="flex gap-2 items-center">
                  {canEdit && onDecrement && onIncrement ? (
                    <>
                      <button
                        onClick={() => onDecrement(currentWatchItem)}
                        className="w-6 h-6 bg-accentLight rounded-full flex items-center justify-center"
                      >
                        <MinusIcon />
                      </button>
                      <span className="text-softText">
                        {anime.episodes || '??'} / {currentWatchItem.progress}
                      </span>
                      <button
                        onClick={() => onIncrement(currentWatchItem)}
                        className="w-6 h-6 bg-accentLight rounded-full flex items-center justify-center"
                      >
                        <PlusIcon />
                      </button>
                    </>
                  ) : (
                    <span className="text-softText">
                      {currentWatchItem.progress} / {anime.episodes || '??'}
                    </span>
                  )}
                </div>
              )}

              <button onClick={handleEditWatchlist}>
                <EditWatchlist />
              </button>
              <span className="w-full h-[2px] bg-[rgba(255,255,255,0.15)]"></span>

              <button onClick={handleWriteReview}>
                {userAlreadyReviewed ? <EditReview /> : <AddReview />}
              </button>
            </>
          )}

          {anime.streaming.find(
            (s) => s.name === 'Crunchyroll' || s.name === 'Netflix'
          ) && (
            <div className="flex flex-col hover:scale-105 transform duration-300 items-center">
              <a
                href={
                  anime.streaming.find((s) => s.name === 'Crunchyroll')?.url ||
                  anime.streaming.find((s) => s.name === 'Netflix')?.url ||
                  '#'
                }
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center"
                onClick={(e) => {
                  if (!loggedInUser) {
                    e.preventDefault()
                    dispatch(openNotRegisteredPopup())
                  }
                }}
              >
                <WatchingOnline />
              </a>
            </div>
          )}
        </div>
      </div>

      {showReviewPopup && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <ReviewPopup
            initialData={initialReviewData}
            animeId={anime.anizone_id}
            loggedInUser={loggedInUser}
            onClose={() => setShowReviewPopup(false)}
          />
        </div>
      )}

      {showEditWatchlist && currentWatchItem && editData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <AnimeWatchlistEdit
            item={currentWatchItem}
            editData={editData}
            setEditData={setEditData}
            handleSaveItemChanges={(updatedItem) => {
              onSaveChanges(updatedItem)
              setShowEditWatchlist(false)
            }}
            getUpdatedWatchItem={getUpdatedWatchItem}
            handleCloseEdit={() => setShowEditWatchlist(false)}
            handleOpenReviewPopup={() => {}}
            userAlreadyReviewed={() => false}
            onRemove={(anizoneId) => {
              onRemove(anizoneId)
              setShowEditWatchlist(false)
            }}
          />
        </div>
      )}
    </div>
  )
}
